import Typography, { TypographyProps } from '@material-ui/core/Typography';
import { createMemo } from '@packages/react';
import { ColorType } from '@packages/style/colors';
import React from 'react';
import styled from 'styled-components';
import { color, theme } from '../lib/style/theme';

interface CurrencyProps extends Omit<TypographyProps, 'color' | 'children'> {
    component?: any;
    color?: ColorType;
    currency?: string;
    format?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    red?: boolean;
    prefix?: string;
    suffix?: string;
    children: string | number;
    withoutStyle?: boolean;
}

const Style = styled(({ red, color, ...rest }) => <Typography {...rest} />)`
    && {
        color: ${(props) => (props.red ? theme('colors.accent') : color('color', 'inherit')(props))};
    }
`;

/**
 * @author: Dumitrana Alinus <alin.designstudio@gmail.com>
 * @class: Currency
 * @date:  30.09.2018
 * @time:  15:28
 * @scope:
 */
export const Currency = createMemo<CurrencyProps>(
    (props) => {
        const {
            children = 0,
            currency = 'USD',
            format = 'en-US',
            minimumFractionDigits = 2,
            maximumFractionDigits = 2,
            prefix,
            suffix,
            withoutStyle = false,
            ...rest
        } = props;

        let children$ = children || children === 0 ? parseFloat(`${children}`) : 0;
        if (Number.isNaN(children$)) {
            children$ = 0;
        }

        const nf = new Intl.NumberFormat(format, {
            style: 'currency',
            currency,
            minimumFractionDigits,
            maximumFractionDigits,
        });

        if (withoutStyle) {
            return (
                <>
                    {prefix ? prefix : ''}
                    {nf.format(children$)}
                    {suffix ? suffix : ''}
                </>
            );
        }

        return (
            <Style component={'span'} variant={'subtitle1'} display={'inline'} {...rest}>
                {prefix ? prefix : ''}
                {nf.format(children$)}
                {suffix ? suffix : ''}
            </Style>
        );
    },
    {
        displayName: 'Currency',
    },
);
